import type { DraftLine, Setup } from "./types";

export type ResolvedPrintRouting = {
  routeMode: "all" | "automatic" | "manual";
  printerIds: number[];
  routing: Record<string, number[]>;
};

function setting(setup: Setup, key: string): string {
  return setup.settings.find((row) => row.key === key)?.value ?? "";
}

function parseRoutes(raw: string): Record<string, number[]> {
  try {
    const value = JSON.parse(raw || "{}");
    if (!value || typeof value !== "object" || Array.isArray(value)) return {};
    const result: Record<string, number[]> = {};
    for (const [key, ids] of Object.entries(value)) {
      if (!Array.isArray(ids)) continue;
      result[key] = [...new Set(ids.map(Number).filter((id) => Number.isInteger(id) && id > 0))];
    }
    return result;
  } catch {
    return {};
  }
}

function csvIds(raw: string): number[] {
  return [...new Set(raw.split(",").map(Number).filter((id) => Number.isInteger(id) && id > 0))];
}

export function categoryPrinterRoutes(setup: Setup): Record<string, number[]> {
  return parseRoutes(setting(setup, "category_printer_routes"));
}

export function categoryPrinterFallbackIds(setup: Setup): number[] {
  return csvIds(setting(setup, "category_printer_fallback_ids"));
}

export function categoryPrinterRoutingEnabled(setup: Setup): boolean {
  return setting(setup, "category_printer_routing_enabled") === "1";
}

export function resolveOrderPrintRouting(
  setup: Setup,
  lines: DraftLine[],
  requestedPrinterIds: number[]
): ResolvedPrintRouting {
  const enabledIds = setup.printers.filter((printer) => printer.enabled).map((printer) => printer.id);
  const enabledSet = new Set(enabledIds);
  if (requestedPrinterIds.length) {
    const invalid = requestedPrinterIds.filter((id) => !enabledSet.has(id));
    if (invalid.length) throw new Error("Ο επιλεγμένος εκτυπωτής δεν είναι πλέον διαθέσιμος.");
    return { routeMode: "manual", printerIds: [...new Set(requestedPrinterIds)], routing: {} };
  }
  if (!enabledIds.length) throw new Error("Δεν υπάρχει ενεργός εκτυπωτής.");
  if (!categoryPrinterRoutingEnabled(setup)) {
    return { routeMode: "all", printerIds: enabledIds, routing: {} };
  }

  const configured = categoryPrinterRoutes(setup);
  const fallback = categoryPrinterFallbackIds(setup);
  const routing: Record<string, number[]> = {};
  for (const line of lines) {
    const product = setup.products.find((candidate) => candidate.id === line.productId);
    const targets = product ? (configured[String(product.category_id)]?.length
      ? configured[String(product.category_id)]
      : fallback) : fallback;
    if (!targets.length) {
      throw new Error(`Δεν έχει οριστεί εκτυπωτής για το προϊόν «${line.name}».`);
    }
    for (const printerId of targets) {
      if (!enabledSet.has(printerId)) {
        throw new Error("Μία διαδρομή δείχνει σε ανενεργό ή διαγραμμένο εκτυπωτή.");
      }
      const key = String(printerId);
      routing[key] ??= [];
      if (!routing[key].includes(line.productId)) routing[key].push(line.productId);
    }
  }
  return {
    routeMode: "automatic",
    printerIds: Object.keys(routing).map(Number).sort((a, b) => a - b),
    routing
  };
}
